import React from 'react';
import { useGetMonthlyForecastQuery } from '../../../../../../Calculations/apiCalculationsSkus';
import { useTemplateOrCalculation } from '../../../../../../../hooks/useTemplateOrCalculation';
import { ConfigurationFieldType, TransactionOverviewType } from '@ydistri/api-sdk';
import { SkuData, SkuRedistributionPaneType } from '../../../../redistributionTypes';
import { Spinner } from '@ydistri/ds';
import { Popover } from 'antd';
import { computeFinalMonthOverview, formatFinalMonths } from './SupplyCoverage.lib';

interface SupplyCoverageProps {
  skuId: number;
  skuData: SkuData;
  infoType: SkuRedistributionPaneType;
  supply: number;
  displayType?: 'popover' | 'text';
}

/**
 * Estimates how many months the current supply can cover based on monthly forecast totals.
 * Source and target panes intentionally use different forecast curves: source uses PsMax + ForecastMax,
 * while target uses PtMin + ForecastMin.
 * This reflects opposite redistribution decisions: protect source stock versus estimate target need.
 * The result is shown as a rounded month value, with values at or above the forecast horizon displayed as "6+".
 */
const SupplyCoverage: React.FC<SupplyCoverageProps> = ({
  skuId,
  skuData,
  infoType,
  supply,
  displayType = 'text',
}) => {
  const templateOrCalculation = useTemplateOrCalculation();

  const { data: monthlyForecast, isFetching } = useGetMonthlyForecastQuery({
    skuId,
    calculationId: templateOrCalculation.id,
  });

  if (supply === 0) return null;
  if (isFetching) return <Spinner />;

  const confidenceType =
    infoType === 'Target'
      ? ConfigurationFieldType.PtMinForecastConfidence
      : ConfigurationFieldType.PsMaxForecastConfidence;
  const forecastTransactionType =
    infoType === 'Target'
      ? TransactionOverviewType.ForecastMin
      : TransactionOverviewType.ForecastMax;

  const confidence = parseInt(skuData.config[confidenceType] ?? '75');

  const finalMonthOverview = computeFinalMonthOverview(
    monthlyForecast,
    confidence,
    forecastTransactionType,
    supply,
  );

  const formattedFinalMonths = formatFinalMonths(finalMonthOverview);

  if (displayType === 'text') {
    return formattedFinalMonths.full;
  } else {
    return (
      <Popover title="Inventory coverage" content={formattedFinalMonths.full}>
        ({formattedFinalMonths.compact})
      </Popover>
    );
  }
};

export default SupplyCoverage;
